import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackPromise } from 'react-promise-tracker';
import { QueryKeys } from 'src/constants';
import ApiService from 'src/modules/api-service';
import { DeliveryAddressRequest, DealerListBasicDTO } from 'src/modules/generated/api';

type CreateDeliveryAddressParams = { addressId?: string } & DeliveryAddressRequest;

export const useGetDealers = (enabled = true) =>
  useQuery<DealerListBasicDTO[]>(
    [QueryKeys.dealerList],
    () => ApiService.dealer.dealerControllerGetDealerList().then((res) => res.data),
    { enabled },
  );

export const useGetDeliveryAddresses = (enabled = true) =>
  useQuery(
    [QueryKeys.deliveryAddresses],
    () =>
      trackPromise(ApiService.deliveryAddress.deliveryAddressControllerGetDeliveryAddresses().then((res) => res.data)),
    { enabled },
  );

export const useGetDeliveryAddressesForDealer = (dealerId: string, enabled = true) =>
  useQuery(
    [QueryKeys.deliveryAddresses, dealerId],
    () =>
      trackPromise(
        ApiService.deliveryAddress
          .deliveryAddressControllerGetDeliveryAddressesForDealer(dealerId)
          .then((res) => res.data),
      ),
    { enabled: enabled && !!dealerId },
  );

export const useCreateDeliveryAddressForDealer = (dealerId: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ addressId, ...request }: CreateDeliveryAddressParams) =>
      addressId !== undefined
        ? ApiService.deliveryAddress.deliveryAddressControllerUpdateDeliveryAddressForDealer(
            dealerId,
            addressId,
            request,
          )
        : ApiService.deliveryAddress.deliveryAddressControllerSaveDeliveryAddressForDealer(dealerId, request),
    {
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.deliveryAddresses, dealerId]);
      },
    },
  );
};

export const useDeleteDeliveryAddressForDealer = (dealerId: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (addressId: string) =>
      ApiService.deliveryAddress.deliveryAddressControllerDeleteDeliveryAddressForDealer(dealerId, addressId),
    {
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.deliveryAddresses, dealerId]);
      },
    },
  );
};

export const useSetDefaultAddress = (dealerId?: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (defaultId: string) => ApiService.deliveryAddress.deliveryAddressControllerUpdateDefaultDeliveryAddress(defaultId),
    {
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.deliveryAddresses]);
        if (dealerId) {
          queryClient.invalidateQueries([QueryKeys.deliveryAddresses, dealerId]);
        }
      },
    },
  );
};
